import _ from "lodash";
import { inject, observer } from "mobx-react";
import { toJS } from "mobx";
import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { FixedSizeList } from "react-window";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import className from "../../shared/className";
import { formatDateTime, toLuxon } from "../../shared/dateTimeUtils";
import Icon from "../../shared/Icon";
import Loader from "../../shared/Loader";
import Toggle from "../../shared/Toggle";
import PDF from "../Monitor/PDF";
import DocumentPurgeHours from "./DocumentPurgeHours";
import "./SnippetReport.scss";
import SnippetReportStore from "./SnippetReportStore";
import { SnippetReportSummary } from "./SnippetReportSummary";
import { DateTime } from "luxon";
import { Checkbox } from "@mui/material";
import config from "../../config";
import CheckboxTree from "./CheckboxTree";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faAngleUp,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { reactSelectTheme } from "../../shared/colorScheme";

const reactSwal = withReactContent(Swal);

class SnippetReport extends React.Component {
  columns = [
    {
      accessor: "status",
      Header: "Status",
      formatter: this.ViewLink,
      filterFormatter: (status) => (
        <React.Fragment>
          <StatusIcon key={status} status={status} />
          {Status({ status })}
        </React.Fragment>
      ),
      value: (status) => Status({ status }),
      comparator(a, b) {
        const statuses = ["Unapproved", "Rejected", "Approved", "Saved", null];
        return statuses.indexOf(b.status) - statuses.indexOf(a.status);
      },
      menuWidth: 130,
      sortable: true,
      filterable: true,
    },
    {
      accessor: (s) => `${s.lastName}, ${s.firstName}`,
      Header: "Patient Name",
      formatter: this.patientLink,
      sortable: true,
      filterable: true,
    },
    {
      accessor: "mrn",
      Header: "MRN",
      sortable: true,
      filterable: true,
    },
    {
      Header: "Unit",
      accessor: "unit",
      filterable: false,
      sortable: true,
      single: true,
    },
    {
      Header: "Bed",
      accessor: "location",
      sortable: true,
    },
    {
      Header: "Captured By",
      accessor: "author",
      sortable: true,
      filterable: true,
    },
    {
      Header: "Created Date/Time",
      accessor: "createdTime",
      formatter: CreatedTime,
      comparator: (a, b) => {
        return (
          DateTime.fromISO(b.createdTime) - DateTime.fromISO(a.createdTime)
        );
      },
      sortable: true,
    },
    {
      Header: "Event Description",
      accessor: "eventDescriptions",
      sortable: true,
      filterable: true,
    },
    {
      Header: "Snippet Type",
      accessor: ({ snippetType }) =>
        _.get(
          this.props.store.documentLabels,
          `snippetDocumentTypes[${snippetType}]`,
        ),
      sortable: true,
      filterable: true,
    },
    {
      Header: "Reviewed By",
      accessor: "reviewedBy",
      sortable: true,
      filterable: true,
    },
  ];
  constructor(props) {
    super(props);
    this.store = new SnippetReportStore();
    this.store.expandHeader = false;
    this.store.patientsStore = this.props.patientsStore;
    this.store.setPref = this.props.store.setPref;

    this.store.columns = this.columns.map((column) => ({
      sorted: null,
      filterOptions: null,
      filterValues: [],
      visible: true,
      formatter: _.identity,
      filterFormatter: _.identity,
      value: _.identity,
      ...column,
    }));

    this.summaryRef = React.createRef();

    Promise.all([
      this.store.fetchLocations(),
      this.store.fetchBedLists(),
      this.props.store.prefsPromise,
    ]).then(() => {
      this.store.columns.find((c) => c.accessor === "mrn").Header =
        this.props.store.prefs?.userPreferences?.server?.pm?.mrnLabel || "MRN";
      this.store.loadPrefs(this.props.store.prefs);
      this.store.exportSupervisorReportPromptText =
        this.props.store.prefs?.userPreferences?.server?.pm?.exportSupervisorReportPromptText;
      this.store.exportEnabled =
        this.props.store.prefs?.userPreferences?.server?.pm?.exportSupervisorReport;
    });

    this.viewLinkClick = this.viewLinkClick.bind(this);
    this.ViewLink = this.ViewLink.bind(this);
    this.setReportResolution = this.setReportResolution.bind(this);
    this.openColumnFilter = this.openColumnFilter.bind(this);
    this.exportCSV = this.exportCSV.bind(this);
    this.togglePurgePendingDocuments =
      this.togglePurgePendingDocuments.bind(this);
  }

  componentDidMount() {
    this.resizeObserver = new ResizeObserver((entries) => {
      this.setWindowSize(entries[0].contentRect);
    });
    this.resizeObserver.observe(this.windowPlaceholder);
  }

  componentDidUpdate() {
    if (this.summaryRef.current)
      this.resizeObserver.observe(this.summaryRef.current);
  }
  render() {
    const rows =
      this.store.filteredSnippets.length > 0
        ? this.store.filteredSnippets.slice()
        : [];
    const totalSnippets = rows?.length;
    const Row = ({ index, style }) => {
      const row = rows[index];
      return (
        <div
          key={row.documentId}
          style={style}
          {...className({
            row: 1,
            even: index % 2,
            odd: !index % 2,
          })}
        >
          {this.store.columns.map((column) => (
            <div
              key={column.Header}
              {...className({
                cell: 1,
                hidden: !column.visible,
              })}
            >
              {column.visible
                ? column.formatter.bind(this)(
                    this.store.getCellValue(column, row),
                    row,
                  )
                : null}
            </div>
          ))}
        </div>
      );
    };
    let height = this.store.expandHeader
      ? this.headerRef?.clientHeight
      : this.summaryRef?.current?.clientHeight;
    if (typeof height === "number") {
      height += 20;
    }

    return (
      <div className="snippet-report" onClick={this.handleClick}>
        <div
          {...className({
            drawer: 1,
            collapsible: this.store.query,
            expanded: this.store.expandHeader,
          })}
          ref={(drawer) => (this.drawer = drawer)}
          style={{
            height,
          }}
          onTransitionEnd={() => {
            requestAnimationFrame(() => {
              this.setWindowSize({
                height: this.windowPlaceholder.clientHeight,
                width: this.windowPlaceholder.clientWidth,
              });
            });
          }}
        >
          <header ref={(header) => (this.headerRef = header)}>
            <div
              className="header-inner"
              ref={(header) => (this.headerRef = header)}
            >
              <div className="search-form filters">
                <div className="date-filter-container">
                  {!this.store.isTele && (
                    <div className="date-filter filter">
                      <div className="date start">
                        <label>Date</label>
                        <DatePicker
                          placeholderText="from"
                          selected={
                            this.store.isTele
                              ? DateTime.local().minus({ days: 1 }).toJSDate()
                              : this.store.startDate
                          }
                          onChange={(date) => {
                            this.store.startDate = date
                              ? toLuxon(date).startOf("day").toJSDate()
                              : this.store.defaultStartDate;
                          }}
                          minDate={this.minDate}
                          maxDate={this.store.endDate || new Date()}
                          startDate={this.store.startDate}
                          isClearable={false}
                        />
                      </div>
                      <Icon symbol="dash" />
                      <div className="date end">
                        <DatePicker
                          placeholderText="to"
                          selected={
                            this.store.isTele ? undefined : this.store.endDate
                          }
                          onChange={(date) => {
                            this.store.endDate = date
                              ? toLuxon(date).endOf("day").toJSDate()
                              : null;
                          }}
                          minDate={this.store.startDate}
                          maxDate={new Date()}
                          endDate={this.store.endDate}
                          isClearable
                        />
                      </div>
                    </div>
                  )}

                  {this.props.store.prefs?.userPreferences?.server?.pm
                    ?.documentPurgeHours ? (
                    <div className="archived-toggle">
                      <label>
                        <Checkbox
                          checked={this.store.includePurgePending}
                          onChange={this.togglePurgePendingDocuments}
                          label="Include Archived Documents"
                          sx={{
                            // TODO: make a global or shared mui theme.
                            "&.Mui-checked": {
                              color: "#1cacdd",
                            },
                          }}
                          className="toggle"
                        />
                        Include Archived Documents *
                      </label>
                      <DocumentPurgeHours
                        hours={
                          this.props.store.prefs?.userPreferences?.server?.pm
                            ?.documentPurgeHours
                        }
                      />
                    </div>
                  ) : null}

                  {config.pm.showTeleCandidate && (
                    <div className="archived-toggle">
                      <label style={{ paddingTop: "8px" }}>
                        <Checkbox
                          style={{ paddingTop: "8px" }}
                          checked={this.store.isTele}
                          onChange={this.toggleTele}
                          label="All patients on a monitor for 48 hours, with only NSR in the last 24 hours."
                          sx={{
                            // TODO: make a global or shared mui theme.
                            "&.Mui-checked": {
                              color: "#1cacdd",
                            },
                          }}
                          className="toggle"
                        />
                        All patients on a monitor for 48 hours,
                        <br /> with only NSR in the last 24 hours.
                      </label>
                    </div>
                  )}
                </div>

                <div className="unit-filter">
                  <label>
                    {_.get(this.store.facilities, "length") > 1
                      ? "Facility - "
                      : ""}{" "}
                    Unit
                  </label>
                  {this.props.store.prefs ? (
                    <CheckboxTree
                      checkModel="all"
                      showExpandAll={true}
                      nodes={toJS(this.store.locations)}
                      checked={this.store.checkedLocations}
                      expanded={this.store.expandedLocations.slice()}
                      onCheck={(checked) => {
                        Object.assign(this.store, {
                          checkedLocations: checked,
                          selectedBedList: null,
                        });
                      }}
                      onExpand={(expanded) => {
                        this.store.expandedLocations = expanded;
                        this.drawer.style.height = "auto";
                        setTimeout(() => {
                          this.drawer.style.height = `${
                            this.headerRef.clientHeight + 20
                          }px`;
                        }, 100);
                      }}
                    />
                  ) : null}
                  {this.store.bedLists?.length ? (
                    <Select
                      className="select"
                      options={this.store.bedLists.slice()}
                      value={this.store.selectedBedList}
                      defaultValue={this.store.selectedBedList}
                      onChange={(v) => {
                        Object.assign(this.store, {
                          selectedBedList: v,
                          checkedLocations: [],
                        });
                      }}
                      getOptionLabel={(o) => o.listName}
                      getOptionValue={(o) => o.id}
                      isClearable={true}
                      placeholder="Select Bed List..."
                      theme={reactSelectTheme({
                        darkMode: this.props.store.darkMode,
                      })}
                    />
                  ) : null}
                  <SearchButton store={this.store} />
                </div>
              </div>
              <div className="filters">
                <div className="bed-filter filter">
                  <label>Bed</label>

                  <div className="bed-range">
                    <div className="start">
                      <input
                        disabled={this.store.query?.selectedBedList}
                        placeholder="from"
                        value={this.store.startBed}
                        onChange={(e) =>
                          Object.assign(this.store, {
                            startBed: e.target.value,
                            selectedBedList: null,
                          })
                        }
                      />
                    </div>
                    <Icon symbol="dash" />
                    <div className="end">
                      <input
                        disabled={this.store.query?.selectedBedList}
                        placeholder="to"
                        value={this.store.endBed}
                        onChange={(e) =>
                          Object.assign(this.store, {
                            endBed: e.target.value,
                            selectedBedList: null,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="actions">
                  {this.store.exportEnabled ? (
                    <button onClick={this.exportCSV} className="btn">
                      Export
                    </button>
                  ) : null}

                  <button onClick={this.openColumnFilter} className="btn">
                    Hide/Show Columns
                  </button>
                </div>
              </div>
            </div>
            {this.store.query ? (
              <SnippetReportSummary
                selectedLocations={this.store.query.simplifiedLocations || []}
                columns={this.store.columns}
                startDate={this.store.query.startDate}
                endDate={this.store.query.endDate}
                startBed={this.store.startBed}
                endBed={this.store.endBed}
                selectedBedList={this.store.query?.selectedBedList}
                total={totalSnippets}
                ref={this.summaryRef}
              />
            ) : (
              <h2 className="snippet-summary">
                Use <SearchButton store={this.store} /> to retrieve reports.
              </h2>
            )}
          </header>
          <div
            className={"drawer-handle"}
            onClick={() => {
              if (this.store.query) {
                this.store.expandHeader = !this.store.expandHeader;
              } else {
                return this.store.isTele
                  ? this.store.fetchTeleCandidates()
                  : this.store.fetchReports();
              }
            }}
          >
            {this.store.query ? (
              <FontAwesomeIcon
                icon={this.store.expandHeader ? faAngleUp : faAngleDown}
              />
            ) : (
              <FontAwesomeIcon icon={faSearch} />
            )}
          </div>
        </div>
        {this.store.loading ? (
          <Loader />
        ) : (
          <div className="table">
            <div className="header">
              <div>
                {this.store.columns.map((column) =>
                  column.visible ? (
                    <div
                      key={column.Header}
                      {...className({
                        sortable: column.sortable,
                        sorted: column.sorted,
                        desc: _.get(column.sorted, "order") === "DESC",
                        secondary: _.get(column.sorted, "priority") === 2,
                      })}
                      onClick={() => this.store.setSort(column)}
                    >
                      {column.Header}
                    </div>
                  ) : (
                    <div key={column.Header} className="hidden" />
                  ),
                )}
              </div>
              <div className="filters-row">
                {this.store.columns.map((column) => (
                  <div
                    {...className({ hidden: !column.visible })}
                    key={column.Header}
                  >
                    {column.visible && _.get(column.filterOptions, "length") ? (
                      <Select
                        options={column.filterOptions.slice()}
                        value={
                          column.single
                            ? column.filterValues
                            : column.filterValues.slice()
                        }
                        onChange={(values) => {
                          column.filterValues = values || [];
                          column._filterValues = values.map((v) => v.value);
                          this.store.savePrefs();
                        }}
                        isMulti={!column.single}
                        hideSelectedOptions={false}
                        classNamePrefix="select"
                        className="select"
                        styles={{
                          menu: (provided) => ({
                            ...provided,
                            width: column.menuWidth,
                          }),
                        }}
                        theme={reactSelectTheme({
                          darkMode: this.props.store.darkMode,
                        })}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <div
          ref={(windowPlaceholder) =>
            (this.windowPlaceholder = windowPlaceholder)
          }
          className="window-placeholder"
        >
          <FixedSizeList
            className="list-window"
            height={this.store.windowSize?.height || 1024}
            itemCount={rows.length}
            itemSize={35}
            width={this.store.windowSize?.width || 1280}
          >
            {Row}
          </FixedSizeList>
          {(this.store.snippets.length && !rows.length) ||
          (this.store.teleCandidates.length && !rows.length) ? (
            <h2 className="filter-warning">
              Modify filters to see more search results.
            </h2>
          ) : null}
        </div>
      </div>
    );
  }

  get headerStyle() {
    const style = {};
    return style;
  }

  get minDate() {
    const oldestSnippetDocumentISO =
      this.props.store.prefs?.userPreferences?.server?.pm
        ?.oldestSnippetDocument;
    if (!oldestSnippetDocumentISO) return undefined;
    return DateTime.fromISO(oldestSnippetDocumentISO).startOf("day").toJSDate();
  }
  setWindowSize = ({ height, width }) => {
    if (height & width)
      this.store.windowSize = {
        height,
        width,
      };
  };
  togglePurgePendingDocuments() {
    this.store.includePurgePending = !this.store.includePurgePending;
  }

  toggleTele = () => {
    this.store.isTele = !this.store.isTele;
  };

  patientLink(value, info) {
    if (config.pm.showTeleCandidate)
      return (
        <div
          onClick={(e) => this.patientLinkClick(e, info)}
          className="view-link"
        >
          {value}
        </div>
      );
    return <div>{value}</div>;
  }

  patientLinkClick(e, info) {
    const windowFeatures = "top=100,left=150,width=1200,height=750";
    const pathname =
      window.location.pathname === "/" ? "" : window.location.pathname;
    const NewWindow = window.open(
      `${window.location.origin}${pathname}/#/patient/${info.asmpid}/monitor/events`,
      "monitor-events",
      windowFeatures,
    );
    e.preventDefault();
    NewWindow.location.reload();
  }

  ViewLink(value, snippet) {
    return (
      <a
        className="view-link"
        onClick={(e) => this.viewLinkClick(e, snippet)}
        href={`document/${snippet.documentId}`}
      >
        <StatusIcon status={snippet.status} />
        VIEW
      </a>
    );
  }

  viewLinkClick(e, report) {
    e.preventDefault();
    const id = report.documentId;
    if (report) {
      this.store
        .fetchSnippetReport({
          asmpid: report.asmpid,
          documentid: id,
        })
        .then((res) => {
          const swalConfig = {
            title: "Snippet Report",
            customClass: "snippet-report-modal snippet-preview",
            showCloseButton: true,
            onOpen: this.pdf && this.pdf.renderPdf,
            html: <PDF base64Pdf={res && res.body} showZoomControls={true} />,
          };
          if (
            report.status === "Unapproved" &&
            this.props.store.mayApproveSnippet &&
            !this.store.isTele
          ) {
            Object.assign(swalConfig, {
              showCancelButton: true,
              confirmButtonText: "Approve",
              confirmButtonColor: "#1CACDD",
              cancelButtonText: "Reject",
              cancelButtonColor: "#C83232",
            });
          }
          return reactSwal.fire(swalConfig);
        })
        .then((result) => {
          if (report.status === "Unapproved" && !this.store.isTele) {
            if (result.value) {
              if (this.props.store.mayApproveSnippet)
                this.setReportResolution(report, "Approved");
            } else if (result.dismiss === Swal.DismissReason.cancel) {
              reactSwal
                .fire({
                  title: "Reject Snippet",
                  text: "Are you sure you want to Reject?",
                  showCancelButton: true,
                  confirmButtonText: "Reject",
                  confirmButtonColor: "#C83232",
                  cancelButtonText: "Cancel",
                  cancelButtonColor: "#999",
                })
                .then((result) => {
                  if (result.value) {
                    this.setReportResolution(report, "Rejected");
                  }
                });
            }
          }
        });
    }
  }

  setReportResolution(report, resolution) {
    if (!this.props.store.mayApproveSnippet)
      throw new Error("Not authorized to approve snippets");
    let promise;
    if (resolution === "Approved") {
      promise = this.store.approveSnippet({ id: report.documentId });
    } else if (resolution === "Rejected") {
      promise = this.store.rejectSnippet({ id: report.documentId });
    }
    promise.then(() => {
      report.status = resolution;
      this.store.snippets = [...this.store.snippets];
    });
  }

  openColumnFilter() {
    reactSwal
      .fire({
        title: "Columns",
        showCloseButton: true,
        html: <ColumnFilter columns={this.store.columns} />,
      })
      .then(() => this.store.savePrefs());
  }

  handleExport() {
    if (!this.store.query) return;

    const summary = this.summaryRef.current.textContent.replaceAll(/\s+/g, " ");

    // rows is a 2-dimensional array, representing the table.
    const rows = this.store.compileCSV(summary);

    // Serialize the 2-dimensional array to CSV. Do this once. Do it last.
    // Double quotes are escaped by double-double quotes inside a string in CSV.
    const csv = rows
      .map((cells) =>
        cells.map((x) => `"${x.replaceAll('"', '""')}"`).join(","),
      )
      .join("\n");

    const fileName = `Snippet_Report_${new Date().toISOString()}.csv`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    if (navigator.msSaveBlob) {
      // IE 10+
      navigator.msSaveBlob(blob, fileName);
    } else {
      const link = document.createElement("a");
      if (link.download !== undefined) {
        // feature detection
        // Browsers that support HTML5 download attribute
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        link.style = "visibility:hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  exportCSV() {
    if (this.store.exportSupervisorReportPromptText) {
      reactSwal
        .fire({
          title: "Notice",
          text: this.store.exportSupervisorReportPromptText,
          showCancelButton: false,
        })
        .then(this.handleExport(this.store))
        .then(() =>
          reactSwal.fire({
            title: "Successfully Exported",
            text: this.store.exportMessage,
            showCancelButton: false,
          }),
        );
    } else {
      this.handleExport(this.store);
      reactSwal.fire({
        title: "Successfully Exported",
        text: this.store.exportMessage,
        showCancelButton: false,
      });
    }
  }
}

export default inject("store")(observer(SnippetReport));

function CreatedTime(value) {
  return formatDateTime(value);
}

class ColumnFilter extends React.Component {
  render() {
    return (
      <div>
        {this.props.columns.map((column) => (
          <Toggle
            key={column.Header}
            onChange={(e) => {
              column.visible = e.target.checked;
              this.forceUpdate();
            }}
            checked={column.visible}
            label={column.Header}
          />
        ))}
      </div>
    );
  }
}

export function StatusIcon({ status }) {
  const resolution = status;
  return (
    <span
      {...className({
        todo: 1,
        done: resolution === "Approved",
        rejected: resolution === "Rejected",
        hidden: !resolution || resolution === "Saved",
      })}
    >
      {resolution === "Approved" ? (
        <Icon symbol="checkmark" />
      ) : resolution === "Rejected" ? (
        <Icon symbol="x" />
      ) : null}
    </span>
  );
}

export function Status({ status }) {
  return (
    {
      Unapproved: "Pending",
      Rejected: "Rejected",
      Approved: "Approved",
      Saved: "N/A",
    }[status] || ""
  );
}

function SearchButton({ store }) {
  return (
    <button
      className="search btn"
      onClick={() => {
        store.isTele ? store.fetchTeleCandidates() : store.fetchReports();
      }}
    >
      Search
    </button>
  );
}
